// SPDX-License-Identifier: GPL-3.0-or-later
// Draws the overlay state into the page slots (THEME.md section 9).
// Layout A is the strip (1280 x 146). Layout B is the card (480 x 270). The CSS picks the layout
// from `data-layout`; this module writes the same text for both.
// Every donor string goes in with `textContent`. The overlay shows no amount of money and no code.

import type { FeaturedStatus } from "../src/shared/contracts.ts";
import { formatPoints } from "../src/table/callouts.ts";
import { HOW_TO_SHORT } from "../src/table/howto.ts";
import { modeLabel } from "../src/table/modes.ts";
import { ellipsize, fillList, type ListSlot, type TextSlot } from "./dom.ts";
import type { OverlayState } from "./overlay-model.ts";

/** The donor name is cut at this many characters on the overlay (THEME.md section 9.2). */
export const NAME_MAX_CHARS = 18;

/** The nodes of the overlay page. */
export interface OverlaySlots {
  readonly root: TextSlot;
  readonly banner: TextSlot;
  readonly bannerText: TextSlot;
  readonly live: TextSlot;
  readonly whoName: TextSlot;
  readonly scoreValue: TextSlot;
  readonly ballText: TextSlot;
  readonly multiplierText: TextSlot;
  readonly modeName: TextSlot;
  readonly eventText: TextSlot;
  readonly ballsInPlay: TextSlot;
  readonly howto: ListSlot;
  readonly queueText: TextSlot;
  readonly result: TextSlot;
  readonly resultScore: TextSlot;
  readonly resultThanks: TextSlot;
  readonly thanks: TextSlot;
  readonly thanksText: TextSlot;
}

function put(node: TextSlot, text: string): void {
  if (node.textContent !== text) node.textContent = text;
}

/** The queue line. "TURN" is singular for one. */
export function queueLine(count: number, nextUp: string): string {
  const base = `QUEUE: ${count} ${count === 1 ? "TURN" : "TURNS"} WAITING`;
  return nextUp === ""
    ? base
    : `${base} - NEXT: ${ellipsize(nextUp, NAME_MAX_CHARS).toUpperCase()}`;
}

/** The mode line: the labels of the active modes, or a fixed text. */
export function modeLine(activeModes: readonly string[]): string {
  return activeModes.length > 0
    ? activeModes.map((m) => modeLabel(m)).join(" + ")
    : "NO MODE ACTIVE";
}

/** Which part of the overlay shows now. The order is the order of priority. */
export type OverlayScene = "hidden" | "banner" | "live" | "result" | "thanks" | "idle";

/** Choose the scene. A hidden overlay (operator control) shows nothing at all. */
export function chooseScene(state: OverlayState, nowMs: number): OverlayScene {
  if (state.hidden) return "hidden";
  if (state.banner !== null && nowMs < state.banner.untilMs) return "banner";
  if (state.featured !== null) return "live";
  if (state.result !== null && nowMs < state.result.untilMs) return "result";
  if (state.thanks !== null && nowMs < state.thanks.untilMs) return "thanks";
  return "idle";
}

/** Show only the part of the scene. The root hides while nothing runs. */
function showScene(scene: OverlayScene, slots: OverlaySlots): void {
  slots.root.hidden = scene === "hidden" || scene === "idle";
  slots.banner.hidden = scene !== "banner";
  slots.live.hidden = scene !== "live";
  slots.result.hidden = scene !== "result";
  slots.thanks.hidden = scene !== "thanks";
  // The scene attribute lets the CSS show the right part with no script.
  slots.root.setAttribute("data-scene", scene);
}

/** The ball field of the live scene. */
function ballLine(st: FeaturedStatus | null): string {
  return st !== null && st.ball_number > 0
    ? `BALL ${st.ball_number} / ${st.balls_per_game}`
    : "BALL - / 3";
}

/** Write the live scene: the player, the score, the mode and the last event. */
function renderLive(featured: NonNullable<OverlayState["featured"]>, slots: OverlaySlots): void {
  const { turn, lastEvent } = featured;
  const st = turn.status;
  put(slots.whoName, ellipsize(turn.display_name, NAME_MAX_CHARS));
  put(slots.scoreValue, formatPoints(st?.score ?? 0));
  put(slots.ballText, ballLine(st));
  put(slots.multiplierText, `BONUS ${st?.multiplier ?? 1}X`);
  put(slots.modeName, modeLine(st?.active_modes ?? []));
  put(slots.eventText, lastEvent);
  put(slots.ballsInPlay, `BALLS IN PLAY: ${st?.balls_in_play ?? 0}`);
}

/** Write the cards that show after a turn: the result card and the thank-you line. */
function renderCards(state: OverlayState, slots: OverlaySlots): void {
  if (state.result !== null) {
    put(slots.resultScore, `FINAL SCORE ${formatPoints(state.result.score)}`);
    put(
      slots.resultThanks,
      `THANK YOU, ${ellipsize(state.result.name, NAME_MAX_CHARS).toUpperCase()}`,
    );
  }
  if (state.thanks !== null) {
    const n = state.thanks.turns;
    put(
      slots.thanksText,
      `THANK YOU, ${ellipsize(state.thanks.name, NAME_MAX_CHARS).toUpperCase()}: ${n} FEATURED ${n === 1 ? "TURN" : "TURNS"}`,
    );
  }
}

/** Write the state into the slots. Call it for each frame and each 250 ms. */
export function renderOverlay(state: OverlayState, nowMs: number, slots: OverlaySlots): void {
  showScene(chooseScene(state, nowMs), slots);
  if (state.banner !== null) {
    put(
      slots.bannerText,
      `FEATURED TURN: ${ellipsize(state.banner.name, NAME_MAX_CHARS).toUpperCase()}`,
    );
  }
  if (state.featured !== null) renderLive(state.featured, slots);
  renderCards(state, slots);
  put(slots.queueText, queueLine(state.queueLength, state.nextUp));
}

/** Fill the how-to list once. The Worker sends the short form; the fixed short form is the fallback. */
export function renderHowTo(state: OverlayState, slots: Pick<OverlaySlots, "howto">): void {
  const lines =
    state.howto !== null && state.howto.controls.length > 0 ? state.howto.controls : HOW_TO_SHORT;
  fillList(slots.howto, lines);
}
